import styles from './TravelReceiptsPreview.module.scss';

import { MouseEventHandler, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TravelCostInvoiceKind } from '@work4all/models/lib/Classes/TravelCostInvoiceKind.entity';
import { TravelExpenseReportPaymentMethod } from '@work4all/models/lib/Classes/TravelExpenseReportPaymentMethod.entity';
import { TravelReceipts } from '@work4all/models/lib/Classes/TravelReceipts.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { formatCurrency, formatNumberAsCurrency } from '@work4all/utils';

import { useTableStateBag } from '../../../dataDisplay/basic-table';
import { LockProvider } from '../../../hooks/object-lock/LockProvider';
import { MIME_TYPES } from '../../../preview/Preview';
import { ProjectPicker } from '../../entity-picker/project-picker/ProjectPicker';
import { formaters } from '../components/formaters';
import {
  PreviewContent,
  PreviewContentField,
} from '../components/PreviewContent';
import { DocumentPreview } from '../document-preview/DocumentPreview';
import { useEntityPreviewData } from '../hooks/use-entity-preview-data';

import { useGetTravelReceiptsStatus } from './use-get-travel-receipts-status';

interface TravelReceiptsPreviewContainerProps
  extends Omit<TravelReceiptsPreviewProps, 'entries'> {
  ids: number[];
}

export const TRAVEL_RECEIPTS_DATA: TravelReceipts<EMode.query> = {
  id: null,
  note: null,
  description: null,
  file: [
    {
      id: null,
      fileInfos: {
        fileServiceProviderInfos: {
          exists: null,
          fspUrl: null,
        },
        previewUrl: null,
        previewMimeType: null,
        downloadUrl: null,
      },
    },
  ],
  projectId: null,
  project: { id: null, name: null, number: null },
  date: null,
  userId: null,
  paymentKind: null,
  expenditure: null,
  currency: {
    id: null,
    name: null,
  },
  travelExpenses: {
    id: null,
    user: {
      id: null,
      displayName: null,
      firstName: null,
      lastName: null,
      shortName: null,
    },
    closedByuserid: null,
    insertionREDate: null,
  },
  receiptKindTravelCost: {
    id: null,
    name: null,
  },
  paymentMethod: {
    id: null,
    name: null,
  },
  travelExpensesId: null,
  receiptKind: {
    id: null,
    name: null,
    typeOfReceiptType: null,
  },
};

export const TravelReceiptsPreviewFields: TravelReceipts<EMode.query> = {
  ...TRAVEL_RECEIPTS_DATA,
};

export function TravelReceiptsPreviewContainer(
  props: TravelReceiptsPreviewContainerProps
) {
  const { ids, ...handlers } = props;
  const entries = useEntityPreviewData<TravelReceipts>(
    ids,
    Entities.travelReceipts,
    TravelReceiptsPreviewFields
  );

  if (!entries) {
    return null;
  }

  return (
    <LockProvider>
      <TravelReceiptsPreview {...handlers} entries={entries} />
    </LockProvider>
  );
}

interface TravelReceiptsPreviewProps {
  // TODO: THIS COULD BE COMMON PROPERTIES
  entries: TravelReceipts<EMode.entity>[];
  onCloseClicked?: MouseEventHandler<HTMLButtonElement>;
  onEditClicked?: MouseEventHandler<HTMLButtonElement>;
  onShareClicked?: () => void;
}

const TravelReceiptsPreview = (props: TravelReceiptsPreviewProps) => {
  const tableStateBag = useTableStateBag();
  const defaultOnClose = useCallback(
    () => tableStateBag.tableInstance.toggleAllRowsSelected(false),
    [tableStateBag]
  );

  const {
    onCloseClicked = defaultOnClose,
    onEditClicked,
    onShareClicked,
    entries,
  } = props;

  const { t } = useTranslation();
  const getTravelReceiptsStatus = useGetTravelReceiptsStatus();

  const fields = useMemo<PreviewContentField<TravelReceipts>[]>(() => {
    return [
      {
        key: 'description',
        label: t('INPUTS.DESCRIPTION'),
      },
      {
        key: 'projectId',
        label: t('COMMON.PROJECT'),
        formater: (_, entry) => entry.project?.name,
        disabled: false,
        getPicker: ({ entry, onEdit, isCommon }) => (
          <ProjectPicker
            multiple={false}
            value={isCommon ? entry.project : null}
            onChange={(value) =>
              onEdit({
                project: value,
                projectId: value ? value.id : 0,
              })
            }
          />
        ),
      },
      {
        key: 'userId',
        label: t('COMMON.SUBMITTER'),
        formater: (_, entry) => entry.travelExpenses?.user?.displayName,
      },
      {
        key: 'date',
        label: t('FIELDS.date'),
        formater: formaters.dateSimple2year,
      },
      {
        key: 'receiptKind',
        label: t('COMMON.DOCUMENT_TYPE'),
        formater(value: TravelCostInvoiceKind) {
          return value?.name;
        },
      },
      {
        closedByuserid: null,
        insertionREDate: null,
        key: 'id',
        label: t('COMMON.STATUS'),
        formater(_, entry) {
          return getTravelReceiptsStatus(entry.travelExpenses).label;
        },
      },
      {
        key: 'paymentMethod',
        label: t('COMMON.PAYMENT_KIND'),
        formater(value: TravelExpenseReportPaymentMethod) {
          return value?.name;
        },
      },
      {
        key: 'expenditure',
        label: t('COMMON.GROSS'),
        formater(_, entry) {
          return `${formatNumberAsCurrency(entry.expenditure)} ${formatCurrency(
            entry.currency,
            t
          )}`;
        },
      },
    ];
  }, [getTravelReceiptsStatus, t]);

  const { description, fileInfos } = useMemo(() => {
    const entry = entries[0];

    return {
      description: entry?.description,
      fileInfos: entry?.file?.[0]?.fileInfos,
    };
  }, [entries]);

  return (
    <div className={styles['preview-wrapper']}>
      {entries[0] ? (
        <DocumentPreview
          title={description}
          exists={fileInfos?.fileServiceProviderInfos?.exists}
          url={fileInfos?.previewUrl}
          mimeType={fileInfos?.previewMimeType as MIME_TYPES}
          noPreviewUrl={
            fileInfos?.fileServiceProviderInfos?.fspUrl ||
            fileInfos?.downloadUrl
          }
          downloadUrl={fileInfos?.downloadUrl}
          fspUrl={fileInfos?.fileServiceProviderInfos?.fspUrl}
          onCloseClicked={onCloseClicked}
          onEditClicked={onEditClicked}
          onShareClicked={onShareClicked}
        />
      ) : null}

      <div>
        <PreviewContent
          entries={entries}
          entity={Entities.travelReceipts}
          fields={fields}
        />
      </div>
    </div>
  );
};
